/* "Pairs Well With" rows beside More Details on the product page.
 * Replaces Rebuy widget 273801. Cards come from the same velocity feed the
 * slide-cart carousel uses; Add buttons are plain .add-to-cart-btn controls, so
 * the slide cart's delegated handler (and its limit-1 rules) does the adding. */
(() => {
  'use strict';

  const LIMIT = 3;
  const TIMEOUT_MS = 3000;

  document.addEventListener('DOMContentLoaded', () => {
    const mount = document.querySelector('[data-pdp-pairs-well]');
    const list = mount?.querySelector('[data-pdp-pairs-well-list]');
    if (!mount || !list || !mount.dataset.recommendationsUrl) return;
    const pageProductId = String(mount.dataset.productId || '');
    // Analytics: feed label with an immutable version (bump when rules change).
    const feedLabel = `${['similar', 'similar-instock'].includes(mount.dataset.feed) ? mount.dataset.feed : 'pairs'}@v1`;
    function track(event) {
      const url = mount.dataset.eventsUrl;
      if (!url || !navigator.sendBeacon) return;
      try { navigator.sendBeacon(url, JSON.stringify({w: 'pdp', f: feedLabel, e: event})); } catch { /* analytics never breaks the widget */ }
    }
    let viewed = false;
    function watchView() {
      if (viewed || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(entries => {
        if (!viewed && entries.some(entry => entry.isIntersecting && !mount.hidden)) {
          viewed = true;
          observer.disconnect();
          track('view');
        }
      }, {threshold: 0.3});
      observer.observe(mount);
    }
    document.addEventListener('slide-cart:added', event => {
      const props = event.detail?.properties;
      if (props && props._source === 'native-pdp-widget') track('add');
    });

    let feed;          // undefined until the feed settles; null when it failed
    let cartIds = null; // null until the cart is known, so cart items never flash
    let cartFromEvent = false;
    let rendered = '';
    // Release products stay out until public (releases DEV-PLAN). The velocity
    // feed already drops release-pending products; this re-checks each picked
    // card's tags on the storefront as a second guard. handle -> true (pending),
    // false (ok) or a Promise while the read is in flight.
    // Unreadable tags FAIL OPEN here (count as ok) on purpose: this feed is
    // velocity /api/similar, which already excludes release-pending server
    // side, so this check is only a backstop and a storefront blip must not
    // empty the widget. The cross-sell block in sections/product.liquid fails
    // CLOSED instead: its promotions.json has no tags and nothing upstream
    // filters it, so its tag read is the only guard.
    const releasePending = new Map();
    function checkRelease(handle) {
      if (releasePending.has(handle)) return;
      releasePending.set(handle, fetch(`/products/${handle}.js`, {headers: {Accept: 'application/json'}})
        .then(response => response.ok ? response.json() : null)
        .then(product => Array.isArray(product?.tags) && product.tags.some(tag => String(tag).toLowerCase() === 'release-pending'))
        .catch(() => false)
        .then(pending => { releasePending.set(handle, pending); safelyRender(); }));
    }

    const decimalId = (value, type) => typeof value === 'string'
      ? new RegExp(`^gid://shopify/${type}/([1-9]\\d*)$`).exec(value)?.[1]
      : null;

    function historyIds() {
      try {
        const snapshot = globalThis.purchaseLimitHistory?.readSnapshot(document);
        // Map of product id -> units bought in the window; only presence matters here.
        return snapshot?.status === 'ok' && snapshot.quantities instanceof Map ? snapshot.quantities : new Map();
      } catch { return new Map(); }
    }

    function safeImage(value) {
      if (typeof value !== 'string') return null;
      try {
        const url = new URL(value);
        return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
      } catch { return null; }
    }

    function formatPrice(amount, currency) {
      const locale = document.documentElement.lang || undefined;
      for (const currencyDisplay of ['narrowSymbol', 'symbol', 'code']) {
        try {
          return new Intl.NumberFormat(locale, {style: 'currency', currency, currencyDisplay}).format(amount);
        } catch { /* older engines reject narrowSymbol; fall through */ }
      }
      return `${currency} ${amount.toFixed(2)}`;
    }

    // Same acceptance rules as the slide-cart carousel. `eligible: false` is
    // normal for the full-price pool and is deliberately not checked.
    function validCard(rec) {
      if (!rec || typeof rec !== 'object' || rec.availableForSale !== true) return null;
      const productId = decimalId(rec.productId, 'Product');
      const variantId = decimalId(rec.variantId, 'ProductVariant');
      const image = safeImage(rec.imageUrl);
      const amount = rec.price?.amount;
      const currency = rec.price?.currencyCode;
      if (!productId || !variantId || !image || typeof rec.title !== 'string' || !rec.title.trim()) return null;
      if (typeof amount !== 'string' || !/^\d+(?:\.\d+)?$/.test(amount) || !Number.isFinite(Number(amount))) return null;
      if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) return null;
      if (typeof Intl.supportedValuesOf === 'function' && !Intl.supportedValuesOf('currency').includes(currency)) return null;
      const handle = typeof rec.handle === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(rec.handle) ? rec.handle : null;
      return {productId, variantId, image, title: rec.title.trim(), price: formatPrice(Number(amount), currency), handle, limitOne: rec.limitOne === true};
    }

    function hide() {
      mount.hidden = true;
      list.replaceChildren();
      rendered = '';
    }

    function row(card) {
      const item = document.createElement('li');
      item.className = 'pdp-pairs__row';

      const media = document.createElement(card.handle ? 'a' : 'div');
      media.className = 'pdp-pairs__media';
      if (card.handle) { media.href = `/products/${card.handle}`; media.tabIndex = -1; media.setAttribute('aria-hidden', 'true'); }
      const img = document.createElement('img');
      img.src = card.image;
      img.alt = '';
      img.width = 96;
      img.height = 96;
      img.loading = 'lazy';
      img.className = 'pdp-pairs__image';
      media.append(img);

      const text = document.createElement('div');
      text.className = 'pdp-pairs__text';
      const title = document.createElement('p');
      title.className = 'pdp-pairs__name';
      if (card.handle) {
        const link = document.createElement('a');
        link.href = `/products/${card.handle}`;
        link.className = 'pdp-pairs__link';
        link.textContent = card.title;
        title.append(link);
      } else {
        title.textContent = card.title;
      }
      const price = document.createElement('p');
      price.className = 'pdp-pairs__price';
      price.textContent = card.price;
      text.append(title, price);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'add-to-cart-btn pdp-pairs__add';
      button.textContent = 'Add';
      button.setAttribute('aria-label', `Add ${card.title} to cart`);
      Object.assign(button.dataset, {
        productId: card.productId,
        variantId: card.variantId,
        quantity: '1',
        properties: JSON.stringify({_source: 'native-pdp-widget', _feed: feedLabel})
      });
      if (card.limitOne) button.dataset.limit = '1';

      item.append(media, text, button);
      return item;
    }

    function render() {
      if (!Array.isArray(feed) || cartIds === null) {
        if (feed !== undefined) hide(); // failed/empty feed: stay hidden
        return;
      }
      const excluded = new Set(cartIds);
      if (pageProductId) excluded.add(pageProductId);
      const purchased = historyIds();
      const picked = [];
      let unchecked = false;
      for (const rec of feed) {
        const card = validCard(rec);
        if (!card || excluded.has(card.productId) || (card.limitOne && purchased.has(card.productId))) continue;
        if (card.handle) {
          const pending = releasePending.get(card.handle);
          if (pending === true) continue;
          if (pending !== false) { checkRelease(card.handle); unchecked = true; }
        }
        picked.push(card);
        excluded.add(card.productId);
        if (picked.length === LIMIT) break;
      }
      // Render once every picked card's release check has answered; each answer re-renders.
      if (unchecked) return;
      if (!picked.length) { hide(); return; }
      const next = JSON.stringify(picked);
      // Unchanged selection keeps the existing buttons, so a label the slide
      // cart painted (Added!, LIMIT 1 · ALREADY PURCHASED) is not wiped.
      if (next === rendered) return;
      list.replaceChildren(...picked.map(row));
      rendered = next;
      mount.hidden = false;
      watchView();
    }

    function safelyRender() {
      try { render(); } catch { try { hide(); } catch { mount.hidden = true; } }
    }

    function setCart(cart) {
      if (!Array.isArray(cart?.items)) return;
      cartIds = new Set(cart.items.filter(item => item.quantity > 0).map(item => String(item.product_id)));
    }

    document.addEventListener('slide-cart:updated', event => {
      cartFromEvent = true;
      setCart(event.detail?.cart);
      safelyRender();
    });

    // The slide cart announces its first /cart.js too, but read it here as
    // well so the widget does not depend on that request succeeding. A newer
    // slide-cart:updated always wins.
    fetch('/cart.js', {headers: {Accept: 'application/json'}})
      .then(response => response.ok ? response.json() : null)
      .then(cart => { if (!cartFromEvent) { setCart(cart); safelyRender(); } })
      .catch(() => {});

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    fetch(mount.dataset.recommendationsUrl, {signal: controller.signal, credentials: 'omit', headers: {Accept: 'application/json'}})
      .then(response => response.ok ? response.json() : null)
      .then(payload => { feed = Array.isArray(payload?.recommendations) ? payload.recommendations : null; })
      .catch(() => { feed = null; })
      .finally(() => { clearTimeout(timer); safelyRender(); });
  });
})();
